/* What "fill in, never change" means on a care plan, shared by the server's
 * staging refinement and the browser's staging handler.
 *
 * The AI may replace the template's own placeholder text, answer a blank
 * questionnaire line and add new content — including adding to text already
 * there, kept word for word. It may not remove or rewrite anything else: text
 * a clinician typed, a suggestion already accepted, or the template's fixed
 * wording around a placeholder. Both ends decide that with the functions
 * below, so the server's check and the browser's cannot disagree about what a
 * placeholder is.
 *
 * A placeholder is defined FROM THE TEMPLATE, never by shape:
 *
 *   - Narrative (`CarePlan.description`): the literal spans below. They cannot
 *     be derived mechanically — the template mixes whole-line prompts, fixed
 *     labels with a placeholder token after them, and fixed prose that must
 *     never be touched — so they are listed, and a test pins every one against
 *     the shipped templates. An empty-value label such as `Goal:` needs no entry:
 *     writing after its colon removes nothing.
 *   - Questionnaires: derived. For the three AI-proposable questionnaires, every
 *     template line value is either blank or a hint, so a line is unanswered
 *     exactly when it is blank or still reads one of its item's template hints.
 */
import type { Attachment } from '@medplum/fhirtypes'
import { orderBy } from 'lodash-es'
import type { JsonObject } from '#src/json.ts'
import {
  type AnswerHtmlParse,
  answerLineText,
  enumerateAnswerLabels,
  findLabelLine,
  toAnswerLines,
} from '#src/models/care-plans/answerScaffold.ts'
import { CARE_PLAN_QR_TEMPLATES } from '#src/models/care-plans/templates/index.ts'

/* Every placeholder span in the v8 narrative templates
 * (`care-plan-initial-v8.html`, `care-plan-semi-annual-v8.html`). The deleted
 * v7 narrative carried the same line text, so plans seeded from it are covered
 * too. Each comment names the template line the span sits in. */
export const CARE_PLAN_NARRATIVE_PLACEHOLDERS = [
  // Every roster line: "PCP: (enter name)", "RN : (enter name)", … "Center Manager: (enter name)".
  '(enter name)',
  // Every closing line: "The PT will monitor and manage the conditions indicated on the PT assessment dated (enter date)".
  '(enter date)',
  // RN, MSW, PT, OT, RD and HCC summary lines (whole line, trailing &nbsp;).
  'Enter brief summary of assessed needs for this care plan period',
  /* PCP and RT summary lines. Listed without their closing full stop: an
   * anchor that stops short of it is still the whole placeholder, and a full
   * stop the anchor does carry is punctuation, never required back. */
  'Enter summary of PPT assessed needs and priority condition/s for this care plan period',
  'Enter a brief summary of assessed needs for this care plan period including PPT’s level of social engagement in the home',
  // The one bullet under "Participant Priorities of Care" (whole line).
  'bullet as many as apply from assessments',
  // RN's prompt line (whole line).
  'Indicate vitals',
  // "ER Visits in the Last Month: Y/N ; If yes, indicate dates and duration:" — after the label.
  'Y/N ; If yes, indicate dates and duration:',
  // "Hospitalizations in the last 6 months: Y/N; If yes, indicate dates and # of days:" — after the label.
  'Y/N; If yes, indicate dates and # of days:',
  // "Significant Weight Loss or Weight Gain: Yes/No/NA" — after the label.
  'Yes/No/NA',
  // "Family Support/Caregiver Availability : Enter details of support e.g. …" — after the bold label.
  'Enter details of support e.g. who provides care for PPT when PPT is not at PACE Center?',
  // The hint inside "Caregiver Preferences for Care (e.g. …):".
  '(e.g. how to contact, time of day, will they escort PPT to appts.)',
  // Semi-annual only: the bullet under "IDT determined that semi-annual assessments …".
  'Bullet disciplines here',
] as const

interface QuoteFolds {
  [char: string]: string
}

const QUOTE_FOLDS: QuoteFolds = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '‟': '"',
}

/* Folds away what a reader would not see as a difference — whitespace runs
 * (non-breaking spaces included) to one space, curly quotes to straight, NFC —
 * and case. The same folds the editor's anchor matcher applies, so text the
 * editor matched is text this recognises. */
export function foldFillText(text: string): string {
  return readableFold(text).toLowerCase()
}

// Every fold but case, so a message can quote the words as they were written.
function readableFold(text: string): string {
  let folded = ''
  for (const char of text.normalize('NFC')) {
    folded += QUOTE_FOLDS[char] ?? char
  }
  return folded.replace(/\s+/g, ' ').trim()
}

/* One word, one number, or one punctuation mark: the unit fixed wording is
 * kept in. Whitespace is never a token, so `RN : ` and `RN: ` are the same
 * wording, while `Jane` and `Janet` — or `4/22/26` and `4/22/261` — are not. */
const TOKEN = /[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu

const WORDLIKE = /[\p{L}\p{N}]/u

interface Token {
  // Lower-cased, for comparison.
  key: string
  // Where it sits in the readable fold, for quoting it back.
  start: number
  end: number
}

function tokenize(readable: string): Token[] {
  return [...readable.matchAll(TOKEN)].map((match) => ({
    key: match[0].toLowerCase(),
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }))
}

function keysOf(text: string): string[] {
  return tokenize(readableFold(text)).map((token) => token.key)
}

interface PlaceholderTokens {
  span: string
  keys: string[]
  /* Whether an anchor may clip this span's words at its edge. Only the care
   * plan's listed spans: each is placeholder text through and through, so any
   * three of its words at an anchor's edge are still placeholder. A visit
   * note's spans are whole prompt lines of its template, whose opening or
   * closing words ("consult with MSW") read just as well at the edge of a
   * clinician's own sentence — so such a line counts only when the anchor
   * holds every word of it, and only punctuation may be clipped. */
  clippable: boolean
}

// Keyed by tokens, as spans are compared, so a restated copy still matches.
const CLIPPABLE_SPANS = new Set(
  CARE_PLAN_NARRATIVE_PLACEHOLDERS.map((span) => keysOf(span).join(' '))
)

/* Tokenized once per list: the list is the care plan's constant, or the one a
 * call was stamped with, so the same array comes back on every check. */
const tokenizedLists = new WeakMap<readonly string[], PlaceholderTokens[]>()

function placeholderTokens(
  placeholders: readonly string[]
): PlaceholderTokens[] {
  const cached = tokenizedLists.get(placeholders)
  if (cached) {
    return cached
  }
  const tokens = placeholders
    .flatMap((span) => {
      const keys = keysOf(span)
      return keys.length > 0
        ? [{ span, keys, clippable: CLIPPABLE_SPANS.has(keys.join(' ')) }]
        : []
    })
    // Longest first, so a span is never claimed by a shorter one inside it.
    .sort((a, b) => b.keys.length - a.keys.length)
  tokenizedLists.set(placeholders, tokens)
  return tokens
}

/* The fewest tokens an anchor may clip a placeholder down to at its edge and
 * still have the clipped part count as placeholder: `Y/N` is three, and so is
 * `Enter brief summary`. One or two would let a clinician's own last word —
 * a bare `Yes` — pass for the start of `Yes/No/NA`. */
const MIN_CLIPPED_TOKENS = 3

function sameKeys(
  a: readonly string[],
  aFrom: number,
  b: readonly string[],
  bFrom: number,
  length: number
): boolean {
  for (let offset = 0; offset < length; offset += 1) {
    if (a[aFrom + offset] !== b[bFrom + offset]) {
      return false
    }
  }
  return true
}

/* Whether clipping `span` down to the part at `[from, from + length)` leaves
 * behind only what a clippable span may lose: any tokens for one, punctuation
 * alone for a whole-line span. */
function clipKeeps(
  { keys: span, clippable }: PlaceholderTokens,
  from: number,
  length: number
): boolean {
  return (
    clippable ||
    ![...span.slice(0, from), ...span.slice(from + length)].some((key) =>
      WORDLIKE.test(key)
    )
  )
}

/* Marks the tokens of `keys` that are template placeholder text: every whole
 * placeholder, and — at the very start or end of what an anchor matched —
 * a placeholder the anchor clipped (for a whole-line span, clipped of
 * punctuation only; see `clippable`). An anchor is a substring of its line, so
 * one that stops short of a placeholder's last words (`… care plan period`
 * without the full stop, `ER Visits in the Last Month: Y/N`) or starts inside
 * one still covers only placeholder text there. */
function claimPlaceholders(
  keys: readonly string[],
  placeholders: readonly PlaceholderTokens[]
): boolean[] {
  const claimed = new Array<boolean>(keys.length).fill(false)
  const free = (from: number, length: number) =>
    !claimed.slice(from, from + length).some(Boolean)
  for (const { keys: span } of placeholders) {
    for (let at = 0; at + span.length <= keys.length; at += 1) {
      if (free(at, span.length) && sameKeys(keys, at, span, 0, span.length)) {
        claimed.fill(true, at, at + span.length)
        at += span.length - 1
      }
    }
  }
  // The anchor's end clipping a placeholder's tail: its last tokens are a
  // placeholder's first ones.
  for (const placeholder of placeholders) {
    const span = placeholder.keys
    for (
      let length = Math.min(span.length - 1, keys.length);
      length >= MIN_CLIPPED_TOKENS;
      length -= 1
    ) {
      const from = keys.length - length
      if (
        clipKeeps(placeholder, 0, length) &&
        free(from, length) &&
        sameKeys(keys, from, span, 0, length)
      ) {
        claimed.fill(true, from, keys.length)
        break
      }
    }
  }
  // The anchor's start clipping a placeholder's head: its first tokens are a
  // placeholder's last ones.
  for (const placeholder of placeholders) {
    const span = placeholder.keys
    for (
      let length = Math.min(span.length - 1, keys.length);
      length >= MIN_CLIPPED_TOKENS;
      length -= 1
    ) {
      if (
        clipKeeps(placeholder, span.length - length, length) &&
        free(0, length) &&
        sameKeys(keys, 0, span, span.length - length, length)
      ) {
        claimed.fill(true, 0, length)
        break
      }
    }
  }
  return claimed
}

// Whether `needle` occurs in `haystack` as a contiguous run from `from` on.
function indexOfRun(
  haystack: readonly string[],
  needle: readonly string[],
  from: number
): number {
  for (let at = from; at + needle.length <= haystack.length; at += 1) {
    if (sameKeys(haystack, at, needle, 0, needle.length)) {
      return at
    }
  }
  return -1
}

/* The fixed wording a replacement would remove: every run of `matched` that is
 * not template placeholder text and does not reappear, whole and in order, in
 * `replacement`, quoted as `matched` spells it. Empty when the replacement only
 * fills placeholders (or keeps everything and adds to it). Both arguments are
 * plain text.
 *
 * Wording is compared token by token — words, numbers, single punctuation
 * marks — with whitespace ignored, so `RN : ` restated as `RN: ` and a trailing
 * non-breaking space never count as a change, but extending `Jane` to `Janet`
 * or gluing `No falls` into `Nofalls` does. A run of punctuation alone (the
 * full stop after `(enter date)`) is not wording and is never required back. */
export function removedNonPlaceholderText(
  matched: string,
  replacement: string,
  /* The placeholder text the document's template offers. The care plan's own
   * by default; a visit note's is its template's prompt lines, and empty for
   * a note with no linked template, where nothing may be replaced — only kept
   * and added to. */
  placeholders: readonly string[] = CARE_PLAN_NARRATIVE_PLACEHOLDERS
): string[] {
  const readable = readableFold(matched)
  const tokens = tokenize(readable)
  const claimed = claimPlaceholders(
    tokens.map((token) => token.key),
    placeholderTokens(placeholders)
  )
  const pieces: Token[][] = [[]]
  for (const [index, token] of tokens.entries()) {
    if (claimed[index]) {
      pieces.push([])
      continue
    }
    pieces.at(-1)?.push(token)
  }
  const target = keysOf(replacement)
  const removed: string[] = []
  let cursor = 0
  for (const piece of pieces) {
    if (!piece.some((token) => WORDLIKE.test(token.key))) {
      continue
    }
    const at = indexOfRun(
      target,
      piece.map((token) => token.key),
      cursor
    )
    if (at === -1) {
      const first = piece[0]
      const last = piece.at(-1)
      removed.push(first && last ? readable.slice(first.start, last.end) : '')
      continue
    }
    cursor = at + piece.length
  }
  return removed
}

interface ReplacementSplit {
  dropped: { start: number; end: number }[]
  kept: number
  additionStart: number
}

/* How a replacement reads against the text it replaces, for showing it: the
 * runs of `matched` it drops, and where in `replacement` the text it adds
 * begins. Offsets are into the two strings as given.
 *
 * Aligned token by token in order — whitespace ignored, case and every other
 * character kept, unlike the fill-only check, since this decides what the
 * clinician sees left in place. Each token of `matched` is kept when the next
 * unclaimed token of `replacement` is the same, and dropped otherwise, so
 * `replacement` always reads as the kept tokens followed by the addition: a
 * replacement that restates its line and adds to it keeps the whole line, and
 * one that fills a placeholder drops just the placeholder. One that rewrites
 * from its first word keeps nothing, and its addition is all of it.
 *
 * A catalog placeholder in `matched` is kept or dropped whole: "Y/N" answered
 * "N" drops the placeholder and adds the "N", rather than keeping the
 * placeholder's own "N" and leaving nothing added. */
export function splitReplacement(
  matched: string,
  replacement: string,
  placeholders: readonly string[] = CARE_PLAN_NARRATIVE_PLACEHOLDERS
): ReplacementSplit {
  const before = readableTokens(matched)
  const after = readableTokens(replacement)
  const atoms = placeholderSpans(before, placeholders)
  const dropped: { start: number; end: number }[] = []
  let kept = 0
  let cursor = 0
  const drop = (token: (typeof before)[number]) => {
    const open = dropped.at(-1)
    // Consecutive dropped tokens are one run, the spaces between them with it.
    if (open && open.end === token.previousEnd) {
      open.end = token.end
    } else {
      dropped.push({ start: token.start, end: token.end })
    }
  }
  for (let index = 0; index < before.length; index++) {
    const atomEnd = atoms.get(index)
    if (atomEnd !== undefined) {
      const atom = before.slice(index, atomEnd)
      if (atom.every((token, k) => after[cursor + k]?.key === token.key)) {
        kept += atom.length
        cursor += atom.length
      } else {
        atom.forEach(drop)
      }
      index = atomEnd - 1
      continue
    }
    const token = before[index]
    if (!token) {
      continue
    }
    if (after[cursor]?.key === token.key) {
      kept += 1
      cursor += 1
      continue
    }
    drop(token)
  }
  return {
    dropped,
    kept,
    additionStart: after[cursor]?.start ?? replacement.length,
  }
}

/* Where each placeholder occurs among `tokens`, as start index to end index
 * (exclusive). Occurrences never overlap: the scan resumes after each. */
function placeholderSpans(
  tokens: readonly { key: string }[],
  placeholders: readonly string[]
): Map<number, number> {
  // Longest first, so a placeholder another one begins with never wins.
  const keyed = orderBy(
    placeholders.flatMap((placeholder) => {
      const keys = readableTokens(placeholder).map(({ key }) => key)
      return keys.length > 0 ? [keys] : []
    }),
    ['length'],
    ['desc']
  )
  const spans = new Map<number, number>()
  for (let index = 0; index < tokens.length; index++) {
    const keys = keyed.find((candidate) =>
      candidate.every((key, k) => tokens[index + k]?.key === key)
    )
    if (keys) {
      spans.set(index, index + keys.length)
      index += keys.length - 1
    }
  }
  return spans
}

// Tokens over the text as given, each compared through every fold but case.
function readableTokens(
  text: string
): { key: string; start: number; end: number; previousEnd: number }[] {
  let previousEnd = -1
  return [...text.matchAll(TOKEN)].map((match) => {
    const start = match.index ?? 0
    const token = {
      key: readableFold(match[0]),
      start,
      end: start + match[0].length,
      previousEnd,
    }
    previousEnd = token.end
    return token
  })
}

/* Whether `proposed` keeps `existing` word for word at its start and then
 * adds something: the one way an answered questionnaire line (or item) may be
 * written to under the fill-only rule. Compared token by token, as kept
 * wording is everywhere here — whitespace ignored, `Jane` never `Janet` — so a
 * value that rewords, trims or re-punctuates what is there is not an append,
 * and neither is one that restates it and adds nothing. */
export function appendsVerbatim(existing: string, proposed: string): boolean {
  const kept = keysOf(existing)
  const next = keysOf(proposed)
  return (
    next.length > kept.length &&
    sameKeys(next, 0, kept, 0, kept.length) &&
    next.slice(kept.length).some((key) => WORDLIKE.test(key))
  )
}

/* The catalog placeholder `piece` is a fragment of, when it is one — for a
 * refusal that must not call placeholder text "not placeholder text". An anchor
 * that clips a placeholder too short at its edge, or cuts one out of its
 * middle, leaves such a fragment behind as "removed". */
export function placeholderContaining(
  piece: string,
  placeholders: readonly string[] = CARE_PLAN_NARRATIVE_PLACEHOLDERS
): string | undefined {
  const keys = keysOf(piece)
  if (!keys.some((key) => WORDLIKE.test(key))) {
    return undefined
  }
  return placeholderTokens(placeholders).find(
    ({ keys: span }) =>
      span.length > keys.length && indexOfRun(span, keys, 0) !== -1
  )?.span
}

/* Whether `text` holds no readable content once folded. */
export function isBlankFillText(text: string): boolean {
  return foldFillText(text) === ''
}

/* The template HTML an item was seeded with, or undefined when the template
 * does not carry that questionnaire or item. */
export function questionnaireTemplateItem(
  questionnaireUrl: string,
  linkId: string
): string | undefined {
  const slug = questionnaireUrl.split('/').pop() ?? ''
  const templates: Readonly<JsonObject> = CARE_PLAN_QR_TEMPLATES.v8
  const section = templates[slug]
  if (!section || typeof section !== 'object' || !('items' in section)) {
    return undefined
  }
  const items: unknown = section.items
  if (!items || typeof items !== 'object') {
    return undefined
  }
  const value: unknown = (items as Readonly<JsonObject>)[linkId]
  return typeof value === 'string' ? value : undefined
}

const labelKey = (text: string) => foldFillText(text).replace(/\s+/g, '')

/* A line's value — its rendered text after its own label's colon — or null
 * when the label asked for is not that line's label.
 *
 * `findLabelLine` matches by PREFIX, so the line it finds for `Status` may be a
 * `Status Update:` line, and slicing the asked-for label's length off it would
 * read `Update: …` (or, for a colon-less `TUG` on a blank `TUG:` line, a bare
 * `:`) as an answer. So the value is cut at the line's own colon, and the label
 * must BE that line's label: exactly, or missing only its colon. */
function valueAfterLabel(lineText: string, label: string): string | null {
  const text = lineText.trim()
  const colon = text.indexOf(':')
  if (colon < 0) {
    return null
  }
  const own = labelKey(text.slice(0, colon + 1))
  const asked = labelKey(label)
  if (asked !== own && `${asked}:` !== own) {
    return null
  }
  return text.slice(colon + 1).trim()
}

/* The hint values an item's template lines carry: for every template line with
 * a label, the text after its first colon, folded. Blank values fold to ''. */
function templateLineValues(
  templateHtml: string | undefined,
  parse: AnswerHtmlParse
): Set<string> {
  const values = new Set<string>([''])
  for (const line of toAnswerLines(templateHtml ?? '', parse)) {
    const text = answerLineText(line, parse).trim()
    const colon = text.indexOf(':')
    if (colon >= 0) {
      values.add(foldFillText(text.slice(colon + 1)))
    }
  }
  return values
}

export type QuestionnaireFillState =
  | 'unanswered'
  | 'answered'
  | 'no-line'
  // A label-less entry over an item whose answer still has labelled lines.
  | 'labelled-item'

export interface QuestionnaireFillTarget {
  questionnaireUrl: string
  linkId: string
  // Absent for an entry that replaces the whole item.
  label?: string | null
  section?: string | null
  // True for a `valueBoolean` entry.
  boolean?: boolean
  // The item's stored answer: HTML for a string item, a boolean, or null.
  currentAnswer: unknown
}

/* Whether the place a questionnaire entry writes still holds nothing a
 * clinician (or an accepted suggestion) put there.
 *
 *   - a boolean item is unanswered only while it has no answer at all;
 *   - a labelled line is unanswered while its value is blank or still reads one
 *     of its item's template hints. The item's whole hint set is compared, not
 *     the one line's, so an answer reshaped by hand — a line added above — does
 *     not turn a hint into "an answer";
 *   - an entry with no label replaces the whole item, so the whole item must be
 *     blank or still be its template, untouched — and, when it still has
 *     labelled lines, the entry is `labelled-item`: replacing the item would
 *     wipe those labels, which are the template's fixed wording, so such an
 *     item is filled one entry per label instead;
 *   - a label that is not a line's own label is `no-line`: nothing to
 *     overwrite, and the staging handler already refuses a label it cannot
 *     place. */
export function questionnaireFillState(
  target: QuestionnaireFillTarget,
  parse: AnswerHtmlParse
): QuestionnaireFillState {
  const { currentAnswer } = target
  if (currentAnswer === null || currentAnswer === undefined) {
    return 'unanswered'
  }
  if (target.boolean) {
    return 'answered'
  }
  if (typeof currentAnswer !== 'string') {
    return 'answered'
  }
  const template = questionnaireTemplateItem(
    target.questionnaireUrl,
    target.linkId
  )
  const label = target.label?.trim()
  if (!label) {
    const current = foldFillText(answerLineText(currentAnswer, parse))
    if (current === '') {
      return 'unanswered'
    }
    if (enumerateAnswerLabels(currentAnswer, parse).length > 0) {
      return 'labelled-item'
    }
    return current === foldFillText(answerLineText(template ?? '', parse))
      ? 'unanswered'
      : 'answered'
  }
  const index = findLabelLine(
    currentAnswer,
    label,
    target.section ?? undefined,
    parse
  )
  if (index === null) {
    return 'no-line'
  }
  const line = toAnswerLines(currentAnswer, parse)[index] ?? ''
  const value = valueAfterLabel(answerLineText(line, parse), label)
  if (value === null) {
    return 'no-line'
  }
  return templateLineValues(template, parse).has(foldFillText(value))
    ? 'unanswered'
    : 'answered'
}

/* The current value of the line a labelled entry addresses, unfolded, for a
 * message that quotes what is already there. Null when there is no such line. */
export function questionnaireLineValue(
  currentAnswer: string,
  label: string,
  section: string | undefined,
  parse: AnswerHtmlParse
): string | null {
  const index = findLabelLine(currentAnswer, label, section, parse)
  if (index === null) {
    return null
  }
  const line = toAnswerLines(currentAnswer, parse)[index] ?? ''
  return valueAfterLabel(answerLineText(line, parse), label)
}

/* Tags that end a line the reader sees, so two paragraphs of an answer never
 * read as one glued word. */
const ANSWER_LINE_ENDING_TAGS =
  /<br\s*\/?>|<\/(p|li|h[1-6]|div|ul|ol|blockquote|tr)>/gi

// Visible text of an answer or a proposed value, a space wherever a line ends.
function answerFillText(html: string, parse: AnswerHtmlParse): string {
  return answerLineText(html.replace(ANSWER_LINE_ENDING_TAGS, ' '), parse)
}

export interface QuestionnaireFillEntry {
  linkId: string
  label?: string | null
  section?: string | null
  valueString?: string | null
  valueBoolean?: boolean | null
}

export type QuestionnaireAnswerValue =
  | string
  | number
  | boolean
  | Attachment
  | null
  | undefined

/* What an answered line or item already reads — the text an append must keep
 * word for word at its start. Null for a boolean, which cannot be appended to,
 * and for a label with no line. */
export function questionnaireExistingText(
  entry: Pick<QuestionnaireFillEntry, 'label' | 'section'>,
  currentAnswer: QuestionnaireAnswerValue,
  parse: AnswerHtmlParse
): string | null {
  if (typeof currentAnswer !== 'string') {
    return null
  }
  const label = entry.label?.trim()
  return label
    ? questionnaireLineValue(
        currentAnswer,
        label,
        entry.section ?? undefined,
        parse
      )
    : answerFillText(currentAnswer, parse)
}

/* An answered line or item may still be ADDED to: the value keeps what is
 * there word for word at its start and appends after it. Anything else —
 * reworded, trimmed, replaced, blanked — changes an existing answer. */
export function appendsToQuestionnaireAnswer(
  entry: QuestionnaireFillEntry,
  currentAnswer: QuestionnaireAnswerValue,
  parse: AnswerHtmlParse
): boolean {
  const existing = questionnaireExistingText(entry, currentAnswer, parse)
  return (
    existing !== null &&
    typeof entry.valueString === 'string' &&
    appendsVerbatim(existing, answerFillText(entry.valueString, parse))
  )
}

/* What an append to an answered line adds, with what the line already held,
 * so the addition can be judged on its own. Null when the entry is not an
 * append to an answered line. */
export function questionnaireAppend(
  entry: QuestionnaireFillEntry,
  currentAnswer: QuestionnaireAnswerValue,
  parse: AnswerHtmlParse
): { existing: string; addition: string } | null {
  const existing = questionnaireExistingText(entry, currentAnswer, parse)
  if (existing === null || typeof entry.valueString !== 'string') {
    return null
  }
  const proposed = answerFillText(entry.valueString, parse)
  if (!appendsVerbatim(existing, proposed)) {
    return null
  }
  return { existing, addition: addedText(existing, proposed, []) }
}

/* What `proposed` adds after the text of `existing` it keeps, without the
 * punctuation that joins it on: "Wears bifocals" to "Wears bifocals. Cataracts"
 * adds "Cataracts". */
export function addedText(
  existing: string,
  proposed: string,
  placeholders?: readonly string[]
): string {
  const { additionStart } = splitReplacement(existing, proposed, placeholders)
  return proposed
    .slice(additionStart)
    .replace(/^[\s.,;:\u2013\u2014-]+/, '')
    .trim()
}

/* Whether writing `entry` where it lands keeps everything that place already
 * holds: the place is unanswered (or has no line to overwrite), or the value
 * adds to its answer word for word. The staging check asks this of the answers
 * a call was made against; an accept asks it again of the answer it is about to
 * write over, which a clinician may have filled in since. */
export function questionnaireEntryOnlyFills(
  questionnaireUrl: string,
  entry: QuestionnaireFillEntry,
  currentAnswer: QuestionnaireAnswerValue,
  parse: AnswerHtmlParse
): boolean {
  const state = questionnaireFillState(
    {
      questionnaireUrl,
      linkId: entry.linkId,
      label: entry.label ?? null,
      section: entry.section ?? null,
      boolean: entry.valueBoolean !== undefined && entry.valueBoolean !== null,
      currentAnswer,
    },
    parse
  )
  if (state === 'labelled-item') {
    return false
  }
  return (
    state !== 'answered' ||
    appendsToQuestionnaireAnswer(entry, currentAnswer, parse)
  )
}
